import { RawAxisProps, SeriesProps } from '@devexpress/dx-react-chart';
import moment from 'moment';
import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardTableModelItem, PregnancyState } from 'shared/api/api';
import { Colors } from 'shared/assets/root-assets/colors';
import { localized } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { WhiteText } from 'web/view/components/Text/white-text';
import PageContainer from 'web/view/components/page-container/page-container';
import { SkioldButton } from 'web/view/components/skiold-components/skiold-button/skiold-button';
import { ViewWeb } from 'web/view/components/utils/web-view';

import { dateFormat, isSameDate } from 'shared/helpers/date-helpers';
import { GetDashboardOverviewGraph } from 'shared/state/ducks/dashboard/operations';
import { ClipLoader } from 'react-spinners';
import { GetMatingBatchReportGraphBySiteAndDate } from 'shared/state/ducks/mating-batch-report/operations';
import { Heading } from '../utils/heading';
import { LineChart } from '../graph/line-graph/line-chart';

interface PropsFromParent {
	closeModal: () => void;
	date: Date;
}

export const BatchReportWeaningLineGraph: FC<PropsFromParent> = React.memo(({ closeModal, date }) => {
	const dispatch = useDispatch();
	// report data
	const weaningReport = useSelector(
		(state: WebAppState) => state.matingBatchReports.matingBatchReportsGraph.weaningReport,
	);
	const { loadingGraph } = useSelector((state: WebAppState) => state.matingBatchReports);
	const matingBathces = useSelector((state: WebAppState) => state.matingBatches.entities);
	const { siteId } = useSelector((state: WebAppState) => state.profile.active!);
	useEffect(() => {
		if (siteId) {
			dispatch(GetMatingBatchReportGraphBySiteAndDate(date, siteId));
		}
	}, [siteId]);

	// Set bar series
	const series = useMemo(() => {
		return [
			{
				argumentField: 'weekYear',
				valueField: 'totalWeanings',
				name: 'totalWeanings',
				color: Colors.yellow,
				key: 'totalWeanings',
				scaleName: 'weanings',
			} as SeriesProps,
			{
				argumentField: 'weekYear',
				valueField: 'avgAlivePerLitter',
				name: 'avgAlivePerLitter',
				color: Colors.pastelBlue,
				key: 'avgAlivePerLitter',
				scaleName: 'pcs',
			} as SeriesProps,
			{
				argumentField: 'weekYear',
				valueField: 'avgWeanedPerWeaninig',
				name: 'avgWeanedPerWeaninig',
				color: Colors.pastelGreen,
				key: 'avgWeanedPerWeaninig',
				scaleName: 'pcs',
			} as SeriesProps,
		];
	}, []);

	//  set YAxis
	const yAxis = useMemo(() => {
		return [
			{ position: 'right', scaleName: 'weanings', showGrid: false } as RawAxisProps,
			{ position: 'left', scaleName: 'pcs' } as RawAxisProps,
		];
	}, []);

	// generate bar chart data
	const data = useMemo(() => {
		if (!weaningReport) {
			return [];
		}

		let data: any[] = [];

		weaningReport.forEach(element => {
			let objectData = { ...element } as any;

			const date = moment(element.matingBatch?.matingPeriodStart)
			const batch =  matingBathces.find(x => isSameDate(x.matingPeriodStart , element.matingBatch?.matingPeriodStart));
			const key = batch?.batchNumber + '-' + date.year();

			objectData.weekYear = key;
			objectData.date = date;

			objectData.TotalBornAvg = Math.round((objectData.AvgAlive + objectData.AvgDead) * 10) / 10;
			data.push(objectData);
		});

		return data.slice().sort((a, b) => a.date - b.date);
	}, [weaningReport]);

	return (
		<PageContainer>
			<Heading text={localized('Weanings')} />
			<ViewWeb className="default-spacing">
				<ViewWeb className="default-close-pos">
					<SkioldButton theme="grey" onPress={closeModal} title={localized('Close')} />
				</ViewWeb>

				{loadingGraph ? (
					<ViewWeb className="spinner-container-active">
						<ClipLoader color="#f2ac40" size={70} />
					</ViewWeb>
				) : (
					<ViewWeb className="flex-row">
						<ViewWeb className="vertical-legend-title">
							<div className="rotate-container">
								<WhiteText className="header-white-text rotate-270">{localized('PigsPcs')}</WhiteText>
							</div>
						</ViewWeb>
						<ViewWeb className="flexOne">
							<LineChart
								lineSeries={series}
								chartData={data}
								valueAxis={yAxis}
								title=""
								yTitle={localized('WeaningsPerBatchPcs')}
								keyTranslator={localized}
								zoomPan="both"
								verticalArgumentLabels={true}
							/>
							<WhiteText className="header-white-text">{localized('matingBatch')}</WhiteText>
						</ViewWeb>
					</ViewWeb>
				)}
			</ViewWeb>
		</PageContainer>
	);
});
